// ============================================================
// AccountShell — the account tier (auth → tenant → workspace):
// pages that need no tenant context (/workspaces, /403) live here.
// ============================================================

import { Link, Outlet } from '@tanstack/react-router';
import {
  Building2,
  LayoutDashboard,
  LogOut,
  PlusCircle,
  Sparkles,
} from 'lucide-react';
import { useAuthActions } from '@/features/auth/hooks/useAuthActions';
import { Button } from '@/components/ui/button';
import { ThemeToggle } from '@/components/theme/theme-toggle';
import { LanguageSwitcher } from '@/components/i18n/LanguageSwitcher';
import { useTranslation } from 'react-i18next';
import { useBranding } from '@/app-config/runtime/use-branding';

export function AccountShell() {
  const { logout, isLoggingOut } = useAuthActions();
  const { t } = useTranslation('shell');
  const branding = useBranding();

  return (
    <div className="flex h-svh flex-col">
      <header className="flex h-14 shrink-0 items-center gap-2 border-b border-border bg-background/80 px-6 backdrop-blur-md supports-[backdrop-filter]:bg-background/60">
        <Sparkles className="size-4 text-primary" aria-hidden="true" />
        <span className="text-sm font-medium">{branding.shortName}</span>
        <span className="text-sm text-muted-foreground">· {t('account')}</span>
        <nav className="ml-auto flex items-center gap-1">
          <Button
            variant="ghost"
            size="sm"
            render={<Link to="/" />}
            nativeButton={false}
          >
            <LayoutDashboard className="size-4" />
            {t('overview')}
          </Button>
          <Button
            variant="ghost"
            size="sm"
            render={<Link to="/workspaces" />}
            nativeButton={false}
          >
            <Building2 className="size-4" />
            {t('workspaces')}
          </Button>
          <Button
            variant="ghost"
            size="sm"
            render={<Link to="/onboarding" />}
            nativeButton={false}
          >
            <PlusCircle className="size-4" />
            {t('createOrJoin')}
          </Button>
          <span className="mx-2 h-4 w-px bg-border" aria-hidden="true" />
          <ThemeToggle />
          <LanguageSwitcher />
          <Button
            variant="ghost"
            size="sm"
            disabled={isLoggingOut}
            onClick={() => void logout()}
          >
            <LogOut className="size-4" />
            {t('logout')}
          </Button>
        </nav>
      </header>
      <main className="min-h-0 flex-1 overflow-y-auto">
        <Outlet />
      </main>
    </div>
  );
}
